import { useState, useMemo } from 'react'
import Image from './Image'
import { InfoIcon } from '@/components/icons'
import ConfirmationModal from '@/components/common/ConfirmationModal'
import SearchAndFilter from './SearchAndFilter'

const Header = ({
    selectedImages,
    onClearSelection,
    searchValue,
    onSearchChange,
    filterFormats,
    onClearFormatFilters,
    onApplyFilters,
}) => {
    return (
        <div className="tw:flex tw:flex-col tw:gap-4">
            <div className="tw:flex tw:justify-between tw:items-center">
                <div className="tw:flex tw:flex-col tw:gap-4">
                    <span className="tw:text-sm tw:font-semibold">Generated Images</span>
                    <div className="tw:text-sm tw:flex tw:gap-2.5 tw:flex tw:items-center">
                        <InfoIcon size={16} style={{ color: '#899CBD' }} />
                        <span>Click on image to select it</span>
                    </div>
                </div>
            </div>
            <SearchAndFilter
                searchValue={searchValue}
                onSearchChange={onSearchChange}
                filterFormats={filterFormats}
                onClearFormatFilters={onClearFormatFilters}
                onApplyFilters={onApplyFilters}
                showClearButton={true}
                disableClearButton={selectedImages.length === 0}
                onClearSelection={onClearSelection}
            />
        </div>
    )
}

const AiImageLibrary = ({
    images,
    selectedImages,
    onClearSelection,
    onSelect,
    onDelete,
    onRefine,
    showHeader,
    handleConfirmDelete,
    handleDeleteConfirmationCancel,
    showDeleteConfirmationModal,
    externalSearchValue,
    externalFilterFormats,
    onExternalSearchChange,
    onExternalApplyFilters,
    onExternalClearFormatFilters,
}) => {
    const [internalSearchValue, setInternalSearchValue] = useState('')
    const [internalFilterFormats, setInternalFilterFormats] = useState([])

    // Use external values if provided, otherwise use internal state
    const searchValue = externalSearchValue !== undefined ? externalSearchValue : internalSearchValue
    const filterFormats = externalFilterFormats !== undefined ? externalFilterFormats : internalFilterFormats
    const setSearchValue = onExternalSearchChange || setInternalSearchValue
    const setFilterFormats = onExternalApplyFilters || ((formats) => setInternalFilterFormats(Array.from(formats)))

    const filteredImages = useMemo(() => {
        let filtered = images

        // Apply search filter
        if (searchValue) {
            filtered = filtered.filter(
                (image) =>
                    image.name?.toLowerCase().includes(searchValue.toLowerCase()) ||
                    image.id?.toLowerCase().includes(searchValue.toLowerCase()),
            )
        }

        // Apply format filter
        if (filterFormats.length > 0) {
            filtered = filtered.filter((image) => {
                // Assuming images have a format property or we can determine from dimensions
                // You may need to adjust this based on your actual image data structure
                if (image.format) {
                    return filterFormats.includes(image.format)
                }
                // Fallback: try to determine from dimensions if available
                if (image.width && image.height) {
                    const aspectRatio = image.width / image.height
                    if (Math.abs(aspectRatio - 1) < 0.1) return filterFormats.includes('square')
                    if (aspectRatio < 1) return filterFormats.includes('vertical')
                    if (aspectRatio > 1) return filterFormats.includes('horizontal')
                }
                return true
            })
        }

        return filtered
    }, [images, searchValue, filterFormats])

    if (images.length === 0) return

    return (
        <div className="tw:mt-6">
            {showHeader && (
                <Header
                    selectedImages={selectedImages}
                    onClearSelection={onClearSelection}
                    searchValue={searchValue}
                    onSearchChange={setSearchValue}
                    filterFormats={filterFormats}
                    onApplyFilters={setFilterFormats}
                    onClearFormatFilters={() => {
                        if (onExternalClearFormatFilters) {
                            onExternalClearFormatFilters()
                        } else {
                            setInternalFilterFormats([])
                        }
                    }}
                />
            )}

            <div className="tw:flex tw:gap-4 tw:flex-wrap tw:mt-6 tw:max-h-140 tw:overflow-auto">
                {filteredImages.map((image) => (
                    <div key={image.id} className="tw:space-y-1">
                        <div className="tw:overflow-hidden">
                            <Image
                                image={image}
                                onSelect={() => onSelect(image.id)}
                                onDelete={() => onDelete([image.id])}
                                onRefine={onRefine ? () => onRefine(image) : undefined}
                                isSelected={selectedImages.some((item) =>
                                    typeof item === 'string' ? item === image.id : item.id === image.id,
                                )}
                            />
                        </div>
                    </div>
                ))}
            </div>
            <ConfirmationModal
                title="Delete"
                variant="danger"
                cancelText="Cancel"
                confirmText="Delete"
                onConfirm={handleConfirmDelete}
                show={showDeleteConfirmationModal}
                onCancel={handleDeleteConfirmationCancel}
                body="Are you sure you want to delete? This action cannot be undone."
            />
        </div>
    )
}

export default AiImageLibrary
